import {
	AfterContentChecked,
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	TemplateRef,
	ViewChild
}                                 from '@angular/core';
import {
	FormControlBase,
	FormControlBaseComponent
}                                 from '@app/core/bases/form-control-base/form-control-base.component';
import { CustomSelectionFactory } from '@app/core/factories/custom-selection-model.factory';
import {
	IFormSelectItem,
	IFormSelectItemTooltipItem
}                                 from '@core/interfaces/form.interface';
import { NgbTooltipConfig }       from '@ng-bootstrap/ng-bootstrap';
import {
	NgSelectComponent,
	SELECTION_MODEL_FACTORY
}                                 from '@ng-select/ng-select';
import { Subject }                from 'rxjs';
import {
	debounceTime,
	take,
	takeUntil
}                                 from 'rxjs/operators';

@Component( {
	selector: 'app-form-select[formEntityId][formGroup][config]',
	templateUrl: './form-select.component.html',
	styleUrls: [ './form-select.component.scss' ],
	providers: [
		{ provide: SELECTION_MODEL_FACTORY, useValue: CustomSelectionFactory },
		NgbTooltipConfig
	]
} )
export class FormSelectComponent extends FormControlBaseComponent implements FormControlBase, AfterViewInit, OnInit, AfterContentChecked {

	private _itemList: IFormSelectItem[];
	private _ngSelect: NgSelectComponent;

	public readonly debouncer: Subject<string> = new Subject<string>();
	// single-select
	public revertValue: string;
	// multi-select
	public revertValues: string[];
	// single-select
	public selectedItem: object;
	// multi-select
	public selectedItems: object[];

	get selectedItemsValues(): any[] {
		return this.selectedItems?.map( ( item ) => item[ this.bindValue ] );
	}

	public tooltipTimeout: ReturnType<typeof setTimeout>;

	@Input() bindLabel: string;
	@Input() bindValue: string;
	@Input() groupBy: string;
	@Input() isLoading: boolean;

	@Input() set itemList( itemList: IFormSelectItem[] ) {
		this._itemList = itemList;
		/*
		 * TODO --check why and if this is really needed here
		 *  (was added in the course of PFISC-7739, possibly due to introduction of
		 *  tooltips for dropdown list items)
		 */
		this.cdref.detectChanges();
	}

	get itemList(): IFormSelectItem[] {
		return this._itemList;
	}

	/** flag for toggling multi-select mode */
	@Input() multiple = false;
	/** Pre-selected items which should stay selected (can't be de-selected) */
	@Input() preserveValuesFromClear: string[];
	@Input() projectOptionsGroupItemTemplateRef: TemplateRef<ElementRef>;
	@Input() projectOptionsItemTemplateRef: TemplateRef<ElementRef>;
	@Input() projectSelectedItemTemplateRef: TemplateRef<ElementRef>;
	@Input() searchable: boolean;
	@Input() tooltipDelayTimer = 1000;
	@Input() virtualScroll: boolean;

	@Output() changeEmitter = new EventEmitter<{}>();
	/** For list filtering via search string input */
	@Output() searchFilterChanged = new EventEmitter<string>();

	@ViewChild( 'ngSelect' ) set ngSelect( ngSelect: NgSelectComponent ) {
		this._ngSelect = ngSelect;
	}

	constructor(
		private cdref: ChangeDetectorRef
	) {
		super();

		this.debouncer
		.pipe(
			takeUntil( this.ngUnsubscribe ),
			debounceTime( 400 )
		)
		.subscribe( ( value ) => this.searchFilterChanged.emit( value ) );
	}

	ngOnInit() {
		super.ngOnInit();
		this.bindLabel = this.bindLabel ? this.bindLabel : 'label';
		this.bindValue = this.bindValue ? this.bindValue : 'text';
		this.itemList = this.itemList ? this.itemList : null;
		this.groupBy = this.groupBy ? this.groupBy : null;
		this.virtualScroll = this.virtualScroll ? this.virtualScroll : false;
		this.searchable = this.searchable ? this.searchable : false;

		this.updateControlState
		?.pipe( takeUntil( this.ngUnsubscribe ) )
		.subscribe( ( shouldUpdate: boolean ) => {
			if ( shouldUpdate ) {
				this.onChange( this.control?.value, false );
			}
		} );

		this.control?.valueChanges
		.pipe( take( 1 ) )
		.subscribe( ( value: any ) => {
			this.onChange( value, false );
		} );
	}

	ngAfterContentChecked() {
		this.cdref.detectChanges();
	}

	ngAfterViewInit(): void {
		super.ngAfterViewInit();
	}

	changeTooltipVisibility( tooltip, isVisible?: boolean, tooltipDataList?: IFormSelectItemTooltipItem[] ): void {
		if ( this.tooltipTimeout ) {
			clearTimeout( this.tooltipTimeout );
			this.tooltipTimeout = null;
		}

		if ( isVisible && tooltipDataList?.length > 0 ) {
			this.tooltipTimeout = setTimeout( () => {
				clearTimeout( this.tooltipTimeout );
				tooltip?.open( { tooltipDataList } );
			}, this.tooltipDelayTimer );
			return;
		}

		tooltip?.close();
	}

	// Overrides method in FormControlBaseComponent
	clearControlValue() {
		if ( this.multiple ) {
			this.updateRevertValue( this.control?.value );
			this.control?.setValue( this.preserveValuesFromClear );
		} else {
			this.updateRevertValue();
			this.control?.setValue( null );
		}

		this.control.markAsDirty();
		this.onChange( null );
	}

	isSelectedItem( value ): boolean {
		return Array.isArray( value )
					 ? this.control?.value?.includes( value )
					 : this.control?.value === value;
	}

	onChange( event, emitEvent: boolean = true ) {
		this.updateActionButtonsVisibility( !!event );
		this.selectedItems = event ?? this.itemList?.filter( item => this.control?.value?.includes( item[ this.bindValue ] ) );

		if ( this.multiple && emitEvent ) {
			this.changeEmitter.emit( this.selectedItems );
		} else {
			if ( event ) {
				this.selectedItem = event;
			}

			if ( emitEvent ) {
				this.changeEmitter.emit( event );
			}
		}
	}

	/**
	 * Sets focus on the actual `NgSelectComponent` host when the form is
	 * navigated via tab key
	 */
	onInputWrapperFocus(): void {
		if ( !this._ngSelect.focused ) { this._ngSelect.focus(); }
	}

	// for multi-select mode
	onRemoveItem( event ): void {
		this.updateRevertValue( [ event?.value?.[ this.bindValue ] ] );
		this.onChange( null );
	}

	onSearchChange( value ) {
		this.debouncer.next( value );
	}

	// Overwrites Base-Class
	revertControlValue() {
		if ( this.multiple ) {
			this.control?.setValue( this.revertValues );
			this.selectedItems = this.itemList?.filter( item => this.revertValues?.includes( item[ this.bindValue ] ) );
			this.revertValues = [];

			this.onChange( this.selectedItems );
		} else {
			this.control?.setValue( this.revertValue );
			this.onChange( this.selectedItem );
		}
	}

	updateRevertValue( revertValues?: string[] ): void {
		if ( this.multiple ) {
			this.revertValues = [];

			// adds preserved values
			if ( this.preserveValuesFromClear?.length ) {
				this.revertValues.push( ...this.preserveValuesFromClear );
			}

			// adds other values from control if not already exist
			this.control?.value?.forEach( controlValue => {
				if ( !this.revertValues?.includes( controlValue ) ) {
					this.revertValues.push( controlValue );
				}
			} );

			// add reset values if not already exist
			revertValues?.forEach( revertValue => {
				if ( !this.revertValues?.includes( revertValue ) ) {
					this.revertValues.push( revertValue );
				}
			} );
		} else {
			if ( this.control?.value ) { this.revertValue = this.control.value; }
		}
	}
}
